import { useState, useCallback, useEffect, useMemo, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
// TODO: add export button when absence report export API is available
// import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import AbsenceBalanceStats from '../../shared/absenceBalanceStats';
import CardsTable from '../../shared/cardsTable';
import NotificationCard from '../../shared/notificationCard';
import SelectField from '../../shared/selectField';
import SectionTitle from '../../shared/sectionTitle';
import Search from '../../shared/search';
// TODO: add export button when absence report export API is available
// import ActionButton from '../../shared/actionButton';
// import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import {
  APP_PAGES,
  ABSENCE_REPORT_USER_STATS_DEFAULT_PARAMS,
} from '../../../constants/pages';
import {
  API_ABSENCE_REPORT_COMPANY_STATS,
  API_ABSENCE_REPORT_USER_STATS,
} from '../../../constants/apiRoutes';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { sticky } from '../../../constants/helperCssRules';
import { absenceSetsSelector } from '../../../store/selectors/absenceSetsSelector';
import {
  getAllAbsenceSets,
  clearAllAbsenceSets,
} from '../../../store/modules/absenceSets';
import {
  hasAuthPermissionCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import {
  prepareAbsenceSetOptions,
  prepareAbsenceTypesOptions,
} from '../../../utility/absence';
import http from '../../../utility/http';
import { useTranslations } from '../../../utility/useTranslations';
import {
  getItemById,
  isArrayEmpty,
  trimString,
} from '../../../utility/helpers';
import {
  hasNextPage,
  parseDuplicateParameters,
} from '../../../utility/uiUtils';
import UserAvatar from '../../shared/userAvatar';
import {
  getTableHeaders,
  prepareAbsenceReportCompanyStatsParams,
  prepareAbsenceReportUserStatsParams,
  prepareAbsenceReportUserStatsRows,
  prepareDetailsUrl,
  prepareTableRows,
} from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  stickyHeader: {
    ...sticky(primary.white, 105),
  },
  description: {
    padding: spacing(8, 0, 6, 0),
  },
  filtersWrapper: {
    display: 'flex',
    alignItems: 'start',
    gap: spacing(3),
    paddingBottom: spacing(6),
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0,300px)) 30px',
    gridColumnGap: spacing(3),
    flex: 1,
  },
  downloadButton: {
    marginTop: spacing(7.75),
  },
  disabledDownloadButton: {
    stroke: primary.bluish5,
  },
  select: {
    width: 300,
  },
  balanceStats: {
    marginBottom: spacing(8),
  },
  search: {
    width: 300,
    marginBottom: spacing(6),
  },
  usersSectionTitle: {
    marginBottom: spacing(2),
  },
  sectionSpacing: {
    marginBottom: spacing(6),
  },
  table: {
    '& .grid-table-header': {
      ...sticky(primary.white, 269, 998),
    },
  },
  userLabel: {
    fontFamily: 'ProximaNova-Bold',
  },
  noResults: {
    marginTop: spacing(3),
  },
}));

const AbsenceReportPage = ({
  auth,
  accessControl,
  navigate,
  dispatch,
  location,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.ABSENCE_REPORT_PAGE);
  const { state } = location;
  const { page: defaultPage } = ABSENCE_REPORT_USER_STATS_DEFAULT_PARAMS;

  const absenceSets = useSelector(absenceSetsSelector);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedAbsenceSet, setSelectedAbsenceSet] = useState(null);
  const [selectedAbsenceType, setSelectedAbsenceType] = useState(null);
  const [search, setSearch] = useState('');
  const [companyStats, setCompanyStats] = useState({});
  const [userStats, setUserStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const pageScrollRef = useRef(null);

  const absenceSetOptions = useMemo(
    () => prepareAbsenceSetOptions(absenceSets),
    [absenceSets]
  );
  const absenceTypesOptions = useMemo(
    () => prepareAbsenceTypesOptions(selectedAbsenceSet?.types ?? []),
    [selectedAbsenceSet?.types]
  );

  const hasMoreRows = hasNextPage(userStats);
  const tableHeaders = useMemo(
    () => getTableHeaders(translations.columns),
    [translations.columns]
  );

  const renderUser = useCallback(
    user => (
      <UserAvatar labelClass={classes.userLabel} user={user} medium caption />
    ),
    [classes.userLabel]
  );

  const rows = useMemo(
    () => prepareAbsenceReportUserStatsRows(userStats?.results, translations),
    [userStats?.results, translations]
  );

  const tableRows = useMemo(
    () => prepareTableRows(rows, renderUser),
    [rows, renderUser]
  );

  const hasDetailsUrlCheck = useCallback(
    user =>
      hasAuthPermissionCheck(PERMISSION_CODES.ABSENCE_VIEW, accessControl) &&
      isUserProfileAccessibleCheck(accessControl, auth.id, user.id),
    [accessControl, auth.id]
  );

  const onAbsenceSetChange = selectedSet => {
    setSelectedAbsenceSet(selectedSet);
    setSelectedAbsenceType(null);
    setCurrentPage(1);
    setIsNextPageLoading(false);
    setCompanyStats({});
    setUserStats({});
  };
  // TODO: add export button when absence report export API is available
  // const onExport = useCallback(() => {
  //   // TODO: download absence report when absence report export API is available
  // }, []);

  const getAbsenceReportCompanyStats = (params = {}) => {
    return http
      .get(API_ABSENCE_REPORT_COMPANY_STATS, {
        params,
        paramsSerializer: data => parseDuplicateParameters(data),
      })
      .then(({ data }) => data);
  };

  const getAbsenceReportUserStats = (params = {}) => {
    return http
      .get(API_ABSENCE_REPORT_USER_STATS, {
        params: {
          ...ABSENCE_REPORT_USER_STATS_DEFAULT_PARAMS,
          ...params,
        },
        paramsSerializer: data => parseDuplicateParameters(data),
      })
      .then(({ data }) => data);
  };

  const getReportData = useCallback(
    async (
      page = defaultPage,
      isLoadMore = false,
      { absenceType = selectedAbsenceType, searchQuery = search } = {}
    ) => {
      if (!absenceType?.id) {
        setCompanyStats({});
        setUserStats({});
        return;
      }

      const companyStatsParams = prepareAbsenceReportCompanyStatsParams({
        selectedAbsenceType: absenceType,
      });
      const userStatsParams = prepareAbsenceReportUserStatsParams({
        selectedAbsenceType: absenceType,
        search: searchQuery,
        page,
      });

      if (isLoadMore) {
        const data = await getAbsenceReportUserStats(userStatsParams);

        return setUserStats(prev => ({
          ...data,
          results: [...(prev.results ?? []), ...(data.results ?? [])],
        }));
      }

      const [companyData, userData] = await Promise.all([
        getAbsenceReportCompanyStats(companyStatsParams),
        getAbsenceReportUserStats(userStatsParams),
      ]);

      setCompanyStats(companyData);
      setUserStats(userData);
    },
    [search, selectedAbsenceType, defaultPage]
  );

  const onAbsenceTypeChange = selectedType => {
    setSelectedAbsenceType(selectedType);
    setCurrentPage(defaultPage);
    setIsNextPageLoading(false);
    getReportData(defaultPage, false, { absenceType: selectedType });
  };

  const onSearch = text => {
    const trimmedSearch = trimString(text);

    setSearch(trimmedSearch);
    setCurrentPage(defaultPage);
    setIsNextPageLoading(false);
    getReportData(defaultPage, false, { searchQuery: trimmedSearch });
  };

  const onLoadMore = useCallback(async () => {
    if (isNextPageLoading || !hasMoreRows) return;

    const nextPage = currentPage + 1;

    try {
      setIsNextPageLoading(true);
      await getReportData(nextPage, true);
      setCurrentPage(nextPage);
    } finally {
      setIsNextPageLoading(false);
    }
  }, [currentPage, getReportData, hasMoreRows, isNextPageLoading]);

  const getInitialData = useCallback(async () => {
    try {
      const absenceSetsData = await getAllAbsenceSets(dispatch);
      const { absenceSetId, absenceTypeId } = state ?? {};
      const initialAbsenceSetOptions =
        prepareAbsenceSetOptions(absenceSetsData);

      let initialAbsenceSet;
      let initialAbsenceType;

      if (absenceSetId && absenceTypeId) {
        initialAbsenceSet = getItemById(
          initialAbsenceSetOptions,
          +absenceSetId
        );
        initialAbsenceType = getItemById(
          prepareAbsenceTypesOptions(initialAbsenceSet?.types ?? []),
          +absenceTypeId
        );
      } else {
        const [firstAbsenceSet] = initialAbsenceSetOptions;
        initialAbsenceSet = firstAbsenceSet;
        const initialAbsenceTypeOptions = prepareAbsenceTypesOptions(
          initialAbsenceSet?.types ?? []
        );
        const [firstAbsenceType] = initialAbsenceTypeOptions;
        initialAbsenceType = firstAbsenceType;
      }

      if (initialAbsenceSet && initialAbsenceType) {
        setSelectedAbsenceSet(initialAbsenceSet);
        setSelectedAbsenceType(initialAbsenceType);
        await getReportData(defaultPage, false, {
          absenceType: initialAbsenceType,
        });
      }
    } finally {
      setIsInitialLoad(false);
    }
  }, [dispatch, getReportData, state, defaultPage]);

  const cleanup = useCallback(() => {
    dispatch(clearAllAbsenceSets());
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const hasRows = !isArrayEmpty(rows);

  return (
    <PageContainer
      {...rest}
      translations={translations}
      location={location}
      auth={auth}
      accessControl={accessControl}
      navigate={navigate}
      shouldPassProps={false}
      pageScrollRef={pageScrollRef}
      isFullWidthContent
    >
      {!isInitialLoad && (
        <>
          <div className={classes.stickyHeader}>
            <div className={classes.description}>
              <Typography variant="body2">
                {translations.description}
              </Typography>
            </div>
            <div className={classes.filtersWrapper}>
              <div className={classes.filters}>
                <SelectField
                  className={classes.select}
                  label={translations.filters.absenceSet.label}
                  placeholder={translations.filters.absenceSet.placeholder}
                  value={selectedAbsenceSet}
                  options={absenceSetOptions}
                  parser={{ value: 'id', label: 'name' }}
                  onChange={onAbsenceSetChange}
                  shouldReturnOption
                  isSearchDisabled
                />
                <SelectField
                  className={classes.select}
                  label={translations.filters.absenceType.label}
                  placeholder={translations.filters.absenceType.placeholder}
                  value={selectedAbsenceType}
                  options={absenceTypesOptions}
                  parser={{ value: 'id', label: 'name' }}
                  onChange={onAbsenceTypeChange}
                  shouldReturnOption
                  isSearchDisabled
                />
                {/* TODO: add export button when absence report export API is available */}
                {/* <ActionButton
                  className={classes.downloadButton}
                  iconClass={classNames({
                    [classes.disabledDownloadButton]: !hasRows,
                  })}
                  type={ACTION_BUTTON_TYPES.DOWNLOAD}
                  tooltipText={translations.exportTooltip}
                  disabledTooltipText={translations.exportTooltipDisabled}
                  isDisabled={!hasRows}
                  onClickHandler={onExport}
                /> */}
              </div>
            </div>
          </div>
          <SectionTitle
            className={classes.sectionSpacing}
            variant="h5"
            title={translations.balanceOverview}
          />
          <AbsenceBalanceStats
            className={classes.balanceStats}
            balance={companyStats}
            translations={translations.balanceStats}
          />
          <SectionTitle
            className={classes.usersSectionTitle}
            variant="h5"
            title={translations.employees}
          />
          <Typography className={classes.sectionSpacing} variant="body2">
            {translations.tableDescription}
          </Typography>
          <Search
            className={classes.search}
            placeholder={translations.filters.search.placeholder}
            value={search}
            onChange={onSearch}
          />
          {hasRows && (
            <CardsTable
              className={classes.table}
              scrollElementRef={pageScrollRef}
              headers={tableHeaders}
              rows={tableRows}
              hasDetailsUrlCheck={hasDetailsUrlCheck}
              prepareDetailsUrl={prepareDetailsUrl}
              hasNextPage={hasMoreRows}
              isNextPageLoading={isNextPageLoading}
              onLoadMore={onLoadMore}
            />
          )}
          <NotificationCard
            className={classes.noResults}
            content={translations.noResults}
            shouldFade={!hasRows}
            shouldFadeOutInstant
          />
        </>
      )}
    </PageContainer>
  );
};

AbsenceReportPage.propTypes = {
  auth: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
};

export default memo(AbsenceReportPage);
